import { useCallback, useEffect, useMemo, useState } from 'react';
import keyBy from 'lodash/keyBy';
import { Box, TablePaginationProps } from '@mui/material';
import { useTablePagination } from 'hooks/useTablePagination';
import { useTeamsPendingDeletionQuery } from 'TeamModule/query/hooks/useTeamsPendingDeletionQuery';
import { TeamPendingDeletion } from 'TeamModule/services/GetTeamsPendingDeletionApi';
import DataDeletionReviewTable from 'TeamModule/components/DataDeletionReviewTable/DataDeletionReviewTable';
import { useUpdateTeamApproverMutation } from 'TeamModule/query/hooks/useUpdateTeamApproverMutation';
import { useRevertTeamApprovalMutation } from 'TeamModule/query/hooks/useRevertTeamApprovalMutation';
import { ApprovalPersonnel } from 'TeamModule/constants/approval';
import {
  SearchTeamsByFieldsPayload,
  TeamSearchSortOrder,
} from 'TeamModule/api/searchTeamsByFieldsApi';
import { useSelector } from 'react-redux';
import { selectAuthUser } from 'selectors';
import { ORDER_DIRECTION } from '@constants';
import { COLUMNS, SORT_BY_MAP } from 'TeamModule/components/DataDeletionReviewTable/columns';
import { buildPendingDeletionSearchParams } from 'TeamModule/components/TeamsV2Page/helpers';

const TeamsV2DeletionTab = ({ filtersPayload }: { filtersPayload: SearchTeamsByFieldsPayload }) => {
  const { pageNumber, handlePageChange, limit } = useTablePagination(20, 1, 1);
  const [selectedIdsState, setSelectedIdsState] = useState<Set<string>>(new Set());
  const [orderBy, setOrderBy] = useState<string>(COLUMNS.DELETION_DATE);
  const [orderDirection, setOrderDirection] = useState<
    (typeof ORDER_DIRECTION)[keyof typeof ORDER_DIRECTION]
  >(ORDER_DIRECTION.ASC);
  const apiParams = useMemo(() => {
    const sortOrderParam: TeamSearchSortOrder =
      orderDirection === ORDER_DIRECTION.ASC ? 'ASC' : 'DESC';
    return buildPendingDeletionSearchParams({
      filtersPayload,
      sortBy: SORT_BY_MAP[orderBy],
      sortOrder: sortOrderParam,
      page: pageNumber,
      limit,
    });
  }, [filtersPayload, limit, orderBy, orderDirection, pageNumber]);

  // A newly submitted search/filter set invalidates the current page and
  // selection — without this, searching from page 3 can land on an empty page.
  useEffect(() => {
    handlePageChange({ newPageNumber: 1 });
    setSelectedIdsState(new Set());
  }, [filtersPayload, handlePageChange]);

  const { teamsPendingDeletion, isLoading } = useTeamsPendingDeletionQuery(apiParams);

  const teams: TeamPendingDeletion[] = useMemo(
    () => (Array.isArray(teamsPendingDeletion?.data) ? teamsPendingDeletion.data : []),
    [teamsPendingDeletion],
  );

  const total = teamsPendingDeletion?.total || 0;

  const allIdsOnPage = useMemo(() => new Set(teams.map((t) => t.id)), [teams]);
  const selectedIds = useMemo(() => new Set(selectedIdsState), [selectedIdsState]);
  const teamById = useMemo(() => keyBy(teams, 'id'), [teams]);
  const selectedCountOnPage = useMemo(
    () => Array.from(allIdsOnPage).filter((id) => selectedIds.has(id)).length,
    [allIdsOnPage, selectedIds],
  );
  const isAllSelectedOnPage = allIdsOnPage.size > 0 && selectedCountOnPage === allIdsOnPage.size;
  const isIndeterminateOnPage = selectedCountOnPage > 0 && selectedCountOnPage < allIdsOnPage.size;
  const hasSelectionOnPage = selectedCountOnPage > 0;

  const toggleSelectAllOnPage = (checked: boolean) => {
    setSelectedIdsState((prev) => {
      const next = new Set(prev);
      if (checked) {
        allIdsOnPage.forEach((id) => next.add(id));
      } else {
        allIdsOnPage.forEach((id) => next.delete(id));
      }
      return next;
    });
  };

  const toggleRow = (rowId: string) => {
    setSelectedIdsState((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

  const { updateTeamApprover, isUpdatingTeamApprover } = useUpdateTeamApproverMutation();
  const { revertTeamApproval, isRevertingTeamApproval } = useRevertTeamApprovalMutation();
  const authUser = useSelector(selectAuthUser);
  const approverName = authUser?.account?.name || '';

  const handleBulkApprove = (type: ApprovalPersonnel) => {
    selectedIds.forEach((rowId) => {
      const team = teamById[rowId];
      if (!team) return;
      updateTeamApprover({
        teamId: team.teamId,
        ...(type === ApprovalPersonnel.Developer
          ? { developerApprover: approverName }
          : { supportApprover: approverName }),
        tenant: team.tenant,
        realmId: team.realmId,
      });
    });
  };

  const handleBulkUnapprove = (type: ApprovalPersonnel) => {
    selectedIds.forEach((rowId) => {
      const team = teamById[rowId];
      if (!team) return;
      revertTeamApproval({
        teamId: team.teamId,
        ...(type === ApprovalPersonnel.Developer ? { developer: 'true' } : { support: 'true' }),
        tenant: team.tenant,
        realmId: team.realmId,
      });
    });
  };

  const onPageChange = useCallback(
    (_event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
      setSelectedIdsState(new Set());
      handlePageChange({ newPageNumber: newPage });
    },
    [handlePageChange],
  );

  const onSort = useCallback(
    (columnId: string) => {
      if (orderBy === columnId) {
        const nextDir =
          orderDirection === ORDER_DIRECTION.ASC ? ORDER_DIRECTION.DESC : ORDER_DIRECTION.ASC;
        setOrderDirection(nextDir);
        setSelectedIdsState(new Set());
      } else {
        const nextDir = ORDER_DIRECTION.DESC;
        setOrderBy(columnId);
        setOrderDirection(nextDir);
        setSelectedIdsState(new Set());
      }
    },
    [orderBy, orderDirection],
  );

  const paginationProps = useMemo<TablePaginationProps & { indexBase: 1 }>(
    () => ({
      count: total,
      page: pageNumber,
      rowsPerPage: limit,
      rowsPerPageOptions: [],
      onPageChange,
      indexBase: 1,
    }),
    [limit, onPageChange, pageNumber, total],
  );

  return (
    <Box>
      <DataDeletionReviewTable
        teams={teams}
        isLoading={isLoading || isUpdatingTeamApprover || isRevertingTeamApproval}
        selectedIds={selectedIds}
        hasSelectionOnPage={hasSelectionOnPage}
        allOnPageSelected={isAllSelectedOnPage}
        isIndeterminateOnPage={isIndeterminateOnPage}
        onToggleRow={toggleRow}
        onToggleSelectAll={toggleSelectAllOnPage}
        paginationProps={paginationProps}
        onBulkApprove={handleBulkApprove}
        onBulkUnapprove={handleBulkUnapprove}
        orderBy={orderBy}
        orderDirection={orderDirection}
        onSort={onSort}
      />
    </Box>
  );
};

export default TeamsV2DeletionTab;
